import { Button, Card, Flex, Tag, theme, Typography } from 'antd'
import {
  AppWindow,
  Clock,
  ExternalLink,
  IdCard,
  KeyRound,
  Link as LinkIcon
} from 'lucide-react'

import type { WorkspaceApp } from 'src/api/facade/types'
import { DateTimeCell, MonoValue } from 'src/components'

import { THIRD_PARTY_CUSTOM_APP_TYPE } from '../constants/constants'
import { useWorkspaceDomainName } from '../hooks/useWorkspaceDomainName'
import { buildAppLaunchUrl } from '../utils/appLaunchUrl'

import { AppIcon } from './AppIcon'
import { useStyles } from './styles'

const { Text, Title, Paragraph } = Typography

type Props = {
  app: WorkspaceApp
}

const isCustomApp = (app: WorkspaceApp) =>
  app.instanceTypeCode === THIRD_PARTY_CUSTOM_APP_TYPE

export const AppDetailHero = ({ app }: Props) => {
  const { styles } = useStyles()
  const { token } = theme.useToken()
  const custom = isCustomApp(app)
  const domainName = useWorkspaceDomainName(app.workspaceId)
  const launchUrl = buildAppLaunchUrl(app, domainName)

  const handleOpenApp = () => {
    if (launchUrl) {
      window.open(launchUrl, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <Card variant="outlined">
      <Flex justify="space-between" gap={16} wrap>
        <Flex gap={14} style={{ minWidth: 0, flex: 1 }}>
          <AppIcon
            type={app.instanceTypeCode}
            photoUrl={app.iconUrl}
            settings={app.settings}
            size={56}
          />
          <Flex vertical gap={6} style={{ minWidth: 0 }}>
            <Flex align="center" gap={8} wrap>
              <Title
                level={3}
                style={{
                  margin: 0,
                  fontSize: 22,
                  fontWeight: 600,
                  lineHeight: 1.2
                }}
              >
                {app.name}
              </Title>
              <Tag className={styles.heroChip}>
                {custom ? 'Custom' : 'Built-in'}
              </Tag>
              {app.isPublic != null ? (
                <Tag
                  className={styles.heroChip}
                  color={app.isPublic ? token.colorPrimary : undefined}
                >
                  {app.isPublic ? 'Public' : 'Private'}
                </Tag>
              ) : null}
            </Flex>

            {app.description?.trim() ? (
              <Paragraph
                type="secondary"
                style={{ margin: 0, fontSize: 13 }}
                ellipsis={{ rows: 2, tooltip: app.description }}
              >
                {app.description}
              </Paragraph>
            ) : null}

            <Flex
              align="center"
              gap={14}
              wrap
              style={{ marginTop: 2, color: 'var(--ant-color-text-secondary)' }}
            >
              {/* The resolved user-facing URL. `app.domain` is deliberately not
                  shown here — it is the API / CORS host, and rendering it beside
                  a link icon read as somewhere you could go. It stays on the
                  Overview tab under an explicit "Domain" label. */}
              {launchUrl ? (
                <Flex align="center" gap={4}>
                  <LinkIcon size={12} aria-hidden />
                  <MonoValue ellipsis>{launchUrl}</MonoValue>
                </Flex>
              ) : null}
              {app.instanceTypeName ? (
                <Flex align="center" gap={4}>
                  <AppWindow size={12} aria-hidden />
                  <Text style={{ fontSize: 12 }}>{app.instanceTypeName}</Text>
                </Flex>
              ) : null}
              {app.createdAt ? (
                <Flex align="center" gap={4}>
                  <Clock size={12} aria-hidden />
                  <Text style={{ fontSize: 12 }}>Installed&nbsp;</Text>
                  <DateTimeCell
                    value={app.createdAt}
                    variant="relative"
                    style={{ fontSize: 12 }}
                  />
                </Flex>
              ) : null}
              <Flex align="center" gap={4}>
                <IdCard size={12} aria-hidden />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  App ID
                </Text>
                <MonoValue>{app.id}</MonoValue>
              </Flex>
              {app.token ? (
                <Flex align="center" gap={4}>
                  <KeyRound size={12} aria-hidden />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    App token
                  </Text>
                  <MonoValue ellipsis>{app.token}</MonoValue>
                </Flex>
              ) : null}
            </Flex>
          </Flex>
        </Flex>

        <Flex gap={6} align="flex-start">
          <Button
            type="primary"
            size="small"
            icon={<ExternalLink size={14} />}
            disabled={!launchUrl}
            onClick={handleOpenApp}
          >
            Open app
          </Button>
        </Flex>
      </Flex>
    </Card>
  )
}
